<template>
  <div class="checkout-wrap">
    <div class="adyen-container">
      <div v-if="!mobile_app_view && pageProps.checkoutLayout">
        <GkLogo :full="true" :fill="'brand'" />
        <div class="checkout-breadcrumbs">
          <a href="/subscriptions/manage">{{ $gettext('Manage subscriptions') }}</a>
        </div>
      </div>

      <h1 v-translate>Your payment</h1>
      <div id="dropin-container" />
    </div>
    <div class="summary-container">

      <svg class="grid-detail" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid-pattern" width="32" height="32" patternUnits="userSpaceOnUse" x="50%"><path d="M0 32V.5H32" fill="none" stroke="currentColor"></path></pattern></defs><rect width="100%" height="100%" fill="url(#grid-pattern)"></rect></svg>

      <h1 v-translate>Your order</h1>
      <p v-if="isNewPaymentMethod" v-translate>In order to set up a new payment method we need to capture a small amount from your account.</p>
      <p v-if="!isNewPaymentMethod">{{ paymentMethodMessage }}</p>
      <b-table
        id="confirm-payment-table"
        :head-variant="'none'"
        thead-class="d-none"
        :fields="tableFields"
        :items="tableItems"
        :tbody-tr-class="rowClass"
        primary-key="id">
        <template #head(total_price)="data">
          <span class="float-right">{{ data.label }}</span>
        </template>
        <template #cell(total_price)="data">
          <div class="vat-row">
            <div>{{ data.item.total_price }}</div>
          </div>
        </template>
      </b-table>
      <div>
        <AddPremiumReceiptAsCost />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import TransactionService from "@/store/services/transactions.service.js";
import PlanTransactionService from "@/store/services/plantransaction.service";
import settingsPageSetup from "@/mixins/settingsPageSetup";
import AddPremiumReceiptAsCost from "@/components/subscriptions/AddPremiumReceiptAsCost";
import GekkoButton from "@/components/GekkoButton";
import CompanyService from "@/store/services/company.service.js"
import GkLogo from "@/components/ui/GkLogo.vue";

//ADYEN
import adyenConfiguration from "@/config/adyen_config.js"
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';

export default {
  name: "ConfirmPayment",
  mixins: [ settingsPageSetup ],
  components: { AddPremiumReceiptAsCost, GekkoButton, GkLogo },
  data () {
    return {
      isCheckout: false,
      pageSettings: {
        title: this.$gettext("Confirm your order"),
        showCompanyPicker: false,
        checkoutLayout: true
      },
      isNewPaymentMethod: false, // false by default
      paymentMethodMessage: "",
      tableFields: [
        { key: "quantity", label: this.$gettext("QUANTITY") },
        { key: "description", label: this.$gettext("DESCRIPTION") },
        { key: "total_price", label: this.$gettext("TOTAL PRICE") }
      ],
      tableItems: [],
      transaction: null,
      shoppingList: null,
      company: null,
      vat_number_error: null,
      vat_number_input: null,
      mobile_app_view: null
    }
  },
  computed: {
    ...mapGetters({ 
      getMobileAppView: "main/getMobileAppView",
    }),
  },
  created () {
    this.mobile_app_view = this.getMobileAppView
    document.body.classList.add('checkout')
    this.loadPaymentData()
  },
  beforeDestroy () {
    document.body.classList.remove('checkout')
  },
  methods: {
    loadPaymentData () {
      this.tableItems = []
      CompanyService.getCurrent().then((response) => {
        this.company = response.data;

        PlanTransactionService.retrieve(this.$route.params['transactionId']).then((response) => {
          this.transaction = response.data;
          this.transaction.shopping_list.order.forEach(item => {
            this.tableItems.push({
              quantity: item.quantity + "x",
              description: item.description,
              total_price: "€" + item.price.toFixed(2),
              _rowVariant: ""
            });
          });
          if(this.transaction.shopping_list.discount){
            var discount = this.transaction.shopping_list.discount;
            this.tableItems.push({ quantity: null, description: discount.description, total_price: "€" + this.$options.filters.formatNumber(discount.price), _rowVariant: "" },)
          }
          let subtotal = this.transaction.shopping_list.subtotal;
          let amount_vat = this.transaction.shopping_list.amount_vat;
          let total_amount = this.transaction.shopping_list.total_amount;
          this.tableItems.push(
            { quantity: null, description: this.$gettext("Subtotal"), total_price: "€" + this.$options.filters.formatNumber(subtotal), _rowVariant: "" },
            { quantity: null, description: this.vatText(), total_price: "€" + this.vatPriceText(amount_vat), _rowVariant: "", remark: "tax_deductible" },
            { quantity: null, description: this.$gettext("Total payment for this period"), total_price: "€" + this.$options.filters.formatNumber(total_amount), _rowVariant: "", class: "total-row", remark: this.$gettext("Payment for this period.") }
          );
          this.initiatePayment()
        }).catch((response)=>{
          if(response.status === 404){
            this.$store.commit('setAlert', {
                title: this.$gettext("Something went wrong with the payment!"),
                text: this.$gettext("We are sorry for this. Please try again."),
                variant: "danger",
                duration: 4
            });
            this.$router.push({name:"Subscriptions"});
          }
        });
      });
    },
    initiatePayment () {
      this.isCheckout = true
      PlanTransactionService.inititateAdyenPaymentSession(this.transaction.id, {}).then((response) => {
        adyenConfiguration.environment = response.data.environment;
        adyenConfiguration.clientKey = response.data.client_key;
        adyenConfiguration.session.id = response.data.id;
        adyenConfiguration.session.sessionData = response.data.sessionData;
        // ADYEN
        // Create an instance of AdyenCheckout using the configuration object.
        AdyenCheckout(adyenConfiguration).then((checkout) => {
          if (this.dropinComponent) {
            this.dropinComponent.update()
          }
          this.checkout = checkout;
          // Create an instance of Drop-in and mount it to the container you created.
          this.dropinComponent = this.checkout.create('dropin').mount('#dropin-container');
        })
      })
    },
    rowClass (item, type) {
      if (!item || type !== 'row') return
      return item.class;
    },
    vatText () {
      if (!this.transaction.vat_reversed_charge)
        return this.transaction.vat + this.$gettext("% VAT (tax deductible)");
      else
        return this.$gettext("VAT (tax deductible)");
    },
    vatPriceText (price) {
      if (!this.transaction.vat_reversed_charge)
        return this.$options.filters.formatNumber(price);
      else
        return ' ' + this.$gettext("reverse charged");    
    },
    validateVATnumber () {
      /* eslint-disable */
      const euVATregex = {
        'AT': (/^(AT)(U\d{8}$)/i),                                   // Austria
        'BE': (/^(BE)(\d{10}$)/i),                                   // Belgium
        'BG': (/^(BG)(\d{9,10}$)/i),                                 // Bulgaria 
        'CY': (/^(CY)([0-5|9]\d{7}[A-Z]$)/i),                        // Cyprus
        'CZ': (/^(CZ)(\d{8,10})?$/i),                                // Czech Republic
        'DE': (/^(DE)([1-9]\d{8}$)/i),                               // Germany 
        'DK': (/^(DK)(\d{8}$)/i),                                    // Denmark 
        'EE': (/^(EE)(10\d{7}$)/i),                                  // Estonia 
        'EL': (/^(EL)(\d{9}$)/i),                                    // Greece 
        'ES': (/^(ES)([0-9A-Z][0-9]{7}[0-9A-Z]$)/i),                 // Spain
        'EU': (/^(EU)(\d{9}$)/i),                                    // EU-type 
        'FI': (/^(FI)(\d{8}$)/i),                                    // Finland 
        'FR': (/^(FR)([0-9A-Z]{2}[0-9]{9}$)/i),                      // France
        'GB': (/^(GB)((?:[0-9]{12}|[0-9]{9}|(?:GD|HA)[0-9]{3})$)/i), // UK (Standard = 9 digits), (Branches = 12 digits), (Government = GD + 3 digits), (Health authority = HA + 3 digits)
        'GR': (/^(GR)(\d{8,9}$)/i),                                  // Greece 
        'HR': (/^(HR)(\d{11}$)/i),                                   // Croatia 
        'HU': (/^(HU)(\d{8}$)/i),                                    // Hungary 
        'IE': (/^(IE)([0-9A-Z\*\+]{7}[A-Z]{1,2}$)/i),                // Ireland
        'IT': (/^(IT)(\d{11}$)/i),                                   // Italy 
        'LV': (/^(LV)(\d{11}$)/i),                                   // Latvia 
        'LT': (/^(LT)(\d{9}$|\d{12}$)/i),                            // Lithunia
        'LU': (/^(LU)(\d{8}$)/i),                                    // Luxembourg 
        'MT': (/^(MT)([1-9]\d{7}$)/i),                               // Malta
        'NL': (/^(NL)(\d{9}B\d{2}$)/i),                              // Netherlands
        'NO': (/^(NO)(\d{9}$)/i),                                    // Norway (not EU)
        'PL': (/^(PL)(\d{10}$)/i),                                   // Poland
        'PT': (/^(PT)(\d{9}$)/i),                                    // Portugal
        'RO': (/^(RO)([1-9]\d{1,9}$)/i),                             // Romania
        'RU': (/^(RU)(\d{10}$|\d{12}$)/i),                           // Russia
        'RS': (/^(RS)(\d{9}$)/i),                                    // Serbia
        'SI': (/^(SI)([1-9]\d{7}$)/i),                               // Slovenia
        'SK': (/^(SK)([1-9]\d[(2-4)|(6-9)]\d{7}$)/i),                // Slovakia Republic
        'SE': (/^(SE)(\d{10}01$)/i)                                  // Sweden
      }
      /* eslint-enable */
      let isValid = false
      const countryRegex = euVATregex[this.company.country]
      if(countryRegex.test(this.vat_number_input) || countryRegex.test(this.company.country + this.vat_number_input)) {
        isValid = true
      }
      return isValid
    },
    _updateVat () {
      if (this.transaction.vat_reversed_charge) {
        this.transaction.vat = 0;
      } else {
        this.transaction.vat = this.transaction.amount * 0.21;
      }
      this.transaction.total = parseFloat(this.transaction.amount) + this.transaction.vat;
    }
  }
}
</script>

<style lang="scss" scoped>
  .row-details {
    background-color: #faf0da;
    padding: 10px;
  }
  .total-row {
    font-family: DINNextLTPro-Medium, sans-serif;
    font-size: 16px;
    color: $colorTextBase;
    > td { vertical-align: bottom; }
    .vat-row {
      font-size: 20px;
      font-family: 'DINNextLTPro-Medium';
    }
  }
</style>

<style lang="scss">
  .checkout-wrap {
    width: 100%;
    height: 100%;
    h1 { margin: 1rem 0; }
    .summary-container, .adyen-container {
      height: 100%;
    }
    .summary-container {
      overflow-x: scroll;
      font-size: 16px;
      > p {
        padding: 0.75rem 0;
      }
      h1 + p { padding-top: 0; }
      .grid-detail {
        display: none;
      }
      #confirm-payment-table {
        margin-bottom: 0.75rem;
        thead {
          font-size: 12px;
          th { white-space: nowrap; }
        }
        tbody { font-size: 14px; }
        .vat-row {
          display: flex; flex-direction: column; text-align: right;
          .remark-text {
            display: block;
            margin-top: 5px;
          }
          .remark-action {
            margin-top: 5px;
            button {
              font-size: 14px;
              padding: 5px 10px;
              line-height: 1.2;
              white-space: nowrap;
            }
          }
        }
      }
      .after-table {
        border-top: 1px solid $colorBorderDash;
        text-align: right;
        .custom-control {
          padding: 1rem 0;
          margin: 0;
        }
      }
    }
    .adyen-container {
      z-index: 1;
      .adyen-checkout__payment-method__name { max-width: 200px; }
      .adyen-checkout__button { background: $colorBrand; }
    }
    @media (min-width: '768px') {
      .summary-container {
        overflow: hidden;
        .grid-detail {
          display: block;
          -webkit-mask-image: linear-gradient(180deg,#fff,transparent);
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0px;
          color: rgba(15,23,42,.025);
          pointer-events: none;
        }
        #confirm-payment-table {
          thead { font-size: 14px; }
          tbody { font-size: 16px; }
          .vat-row {
            .remark-action button { font-size: 16px; }
          }
          .total-row {
            font-size: 18px;
            > td { padding-top: 15px;}
            .vat-row {
              font-size: 24px;
            }
          }
        }
      }
      .adyen-container {
        .adyen-checkout__payment-method__name {
          max-width: 100%;
        }
      }
    }
  .adyen-container, .summary-container { padding: 1rem; }
  @media (min-width: '768px') {
    .adyen-container { padding-bottom: 1.5rem; padding-top: 2.5rem }
    .summary-container { padding-top: 8rem; padding-left: 1rem; }

    .adyen-container {
      width: 40%;
    }
  }
  @media (min-width: '992px') {
    .adyen-container { padding: 1rem 2.5rem; padding-top: 2.5rem }
    .summary-container { padding-left: 2rem; }
    .adyen-container {
      width: 40%;
      max-width: 650px;
      min-width: 550px;
    }
    .adyen-container, .summary-container {
      max-width: 650px;
      min-width: 600px;
    }
  }
  @media (min-width: '1200px') {
    .adyen-container { padding: 2.5rem 5rem; }
    .summary-container { padding-left: 4rem; }
  }
}
</style>
